// xraySubstrates.js — per-tab SUBSTRATE windows for the X-ray lens.
//
// Every X-ray tab used to scan the same newest-N prompt window, so a tab
// whose substrate is sparse in the scope (Artifacts on a Grafana-heavy
// scope) showed an empty window even when the scope held plenty of matching
// prompts further back. Each tab now declares which TOOL NAMES carry its
// substrate; the scan window composes the current scope with a tools-dim
// step over the matching tool ids, so the sampled N prompts are all
// substrate-bearing — "the newest 120 prompts here that touched Grafana",
// not "the newest 120 prompts, hopefully some touched Grafana".
//
// Matchers work on TOOL NAMES only (that's what the tools dimension keys on).
// A name-level match is deliberately a superset of the extractor's op-level
// classification — the window over-samples slightly (any Bash prompt for the
// Git tab) and the extractor stays the precision layer.
//
// NOT a trace<Domain> extractor (no extract/collect/build contract) — this is
// lens config over the extractors' domains, hence the different name.

import { mergeInto } from "../../xray-factory/src/xray/registry.js";
import { xrayRegistry } from "./xrayRegistry.js";
import { parseLinearTool } from "./traceLinearOps.js";
import { SHELL_TOOL_RE } from "./traceForgeOps.js";
import { recallToolOf } from "./traceRecallOps.js";

const lastSegment = (name) => String(name || "").split("__").pop();
const FILE_RE = /^(write|edit|multiedit|notebookedit|apply_patch|create_file|str_replace_editor|write_file|save_file|read|read_file|readfile|notebookread|view|open_file)$/i;
// Name-level mirrors of traceWebResearch's verb sets (those classify with the
// INPUT too; the window only has names).
const RESEARCH_RE = /^(webfetch|web_fetch|fetch_url|url_fetch|fetch_page|firecrawl_scrape|firecrawl_fetch|scrape|scrape_url|navigate|open_url|visit_url|read_url|get_url|websearch|web_search|web_search_call|search_web|brave_search|google_search|duckduckgo_search|tavily_search|tavily_web_search|exa_search|firecrawl_search|deepwiki_search|ask_question)$/i;

const isLinear = (name) => Boolean(parseLinearTool(name));
const isShell = (name) => SHELL_TOOL_RE.test(lastSegment(name));
const isFile = (name) => FILE_RE.test(lastSegment(name));
const isResearch = (name) => RESEARCH_RE.test(lastSegment(name));
const isGrafana = (name) => String(name).startsWith("mcp__") && /grafana/i.test(name);
const isArtifact = (name) => /^artifact$/i.test(lastSegment(name));
const isDataQuery = (name) => {
  const last = lastSegment(name).toLowerCase();
  if (last === "analytics_query") return true;
  if (/emissary/i.test(name) && /^(query_|list_|view_|collect_)/.test(last)) return true;
  // Shell is deliberately NOT part of this substrate: measured on the top
  // tenant (30d), shell-inclusive would be ~94k prompts (≈ all shell, where
  // real SQL is ~4% dense) vs ~900 pure data-query prompts — the superset
  // destroys the filter. Shell SQL still renders when it appears in the
  // sampled prompts' bodies (the extractor's shell door is unchanged); the
  // Git tab remains the shell-complete window.
  return false;
};

// view id → { label (for the status line), match(toolName) }. Tabs absent
// here (terms, behaviours, describe, workflows, prompts) keep the unfiltered
// window.
const HAND_WIRED_SUBSTRATES = {
  files: { label: "file-touching", match: isFile },
  linear: { label: "Linear", match: isLinear },
  research: { label: "web-research", match: isResearch },
  forge: { label: "shell", match: isShell },
  grafana: { label: "Grafana", match: isGrafana },
  dataq: { label: "data-query", match: isDataQuery },
  recall: { label: "Origin-recall", match: (n) => Boolean(recallToolOf(n)) },
  artifacts: { label: "artifact-publishing", match: isArtifact },
};

// The HOST families whose ops become provenance nodes — the keys above that
// `lineageNodes` reads. A list because the host authors these, the same reason
// traceProvenance keeps its own PRODUCTION_KINDS.
const LINEAGE_HOST_KEYS = ["files", "linear", "research", "forge", "grafana", "dataq", "recall", "artifacts"];

// Manifest-declared lenses, merged over the hand-written table above.
//
// ADDITIVE on purpose. A lens migrates to a manifest one at a time rather than in
// one risky change, and a manifest claiming a key the host still owns is REPORTED
// rather than silently winning — a substrate quietly overwritten presents as "this
// tab scans the wrong window", which is the hardest kind of wrong to notice.
//
// Derived LAZILY and memoized on the registry itself, because the catalog now
// includes runtime lenses and is resolved per tenant after `/v1/me`. Keying the
// memo on registry identity rather than a boolean means an invalidated catalog
// rebuilds the table exactly once, and never rebuilds every matcher closure on a
// render.
let _table = null;
let _tableFor = null;

function buildTable(registry) {

// Lineage's window is the UNION of every source that contributes a node to the
// graph — the host families above, plus every lens that declares a `lineage`
// projection, each matched by its OWN substrate.
//
// It was a hand-written `isFile || isLinear || isResearch`, and that was the
// reason the graph looked thin. Measured: the union it named excluded Bash, so
// the nine factory lenses that declare a projection could not appear in the
// window at all — they were not sparse, they were unreachable — and it also shut
// out forge, query, Grafana, recall and artifacts, five host families whose nodes
// the pass builds. A lens joined the graph by declaring a projection and then
// never showed up, which reads as "found nothing" rather than "never sampled".
//
// Derived, so a lens reaches the window by declaring, and the host keeps no list
// of which lenses count. The manifest now requires a substrate from any lens that
// declares a lineage projection, which is what makes this total.
  const lineageMatchers = [
    ...LINEAGE_HOST_KEYS.map((k) => HAND_WIRED_SUBSTRATES[k].match),
    ...registry.lineageSources()
      .map(({ id }) => registry.lens(id)?.substrate?.match)
      .filter((m) => typeof m === "function"),
  ];
  const substrateMerge = mergeInto(
    {
      ...HAND_WIRED_SUBSTRATES,
      lineage: { label: "artifact-lineage", match: (n) => lineageMatchers.some((m) => m(n)) },
    },
    registry.substrates(),
  );
  if (registry.problems.length || substrateMerge.collisions.length) {
    // Loud, never fatal: one bad manifest must not blank the catalog.
    console.warn("[xray] manifest problems:", [
      ...registry.problems,
      ...substrateMerge.collisions.map((c) => `${c}: collides with a hand-wired substrate`),
    ]);
  }
  return substrateMerge.merged;
}

/** view id → {label, match}. Memoized per catalog. */
export function xraySubstrates() {
  const registry = xrayRegistry();
  if (_tableFor !== registry) {
    _table = buildTable(registry);
    _tableFor = registry;
  }
  return _table;
}

// Keep the IN-predicate bounded — a scope can carry hundreds of distinct
// tools; a substrate should never be more than a few dozen.
const SUBSTRATE_IDS_CAP = 100;
const CONTROL_CHARACTER_RE = /[\x00-\x1F\x7F]/;

// The scope step for a tab's substrate, from the scope's own tool entries:
//   { step }          — filter the window with this tools step
//   { empty: true }   — the scope has NO substrate tools at all: skip the
//                       window fetch and show the tab's empty note directly
//   null              — tab has no substrate (or entries unknown): unfiltered
export function substrateStepFor(view, toolEntries) {
  const substrate = xraySubstrates()[view];
  if (!substrate) return null;
  if (!Array.isArray(toolEntries)) return null;
  const ids = [];
  for (const entry of toolEntries) {
    const id = entry?.id != null ? String(entry.id) : null;
    // Tool names come from captured model output. Preserve malformed names in
    // analytics, but never let a control-character-bearing value silently
    // become an automatic X-ray scope predicate.
    if (id && !CONTROL_CHARACTER_RE.test(id) && substrate.match(id)) {
      ids.push(id);
      if (ids.length >= SUBSTRATE_IDS_CAP) break;
    }
  }
  if (!ids.length) return { empty: true, label: substrate.label };
  return { step: { dim: "tools", ids }, label: substrate.label };
}
